import { useEffect, useRef } from "react";
import maplibregl, { StyleSpecification } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CountryAssessments } from "@app/features/other/genericApiSlice";
import {
  ACTIVE_COLOR,
  COUNTRIES_SOURCE,
  COUNTRY_FOCUS_ZOOM,
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  FAO_OCEAN_TILES,
  FLY_DURATION_MS,
  GLYPHS,
  HIT_LAYER,
  HOVER_COLOR,
  INACTIVE_COLOR,
  LABELS_LAYER,
  MARKER_LAYERS,
  MAX_ZOOM,
  MIN_ZOOM,
  VISUAL_LAYER,
} from "../constants";
import countryCentroids from "../countryCentroids";
import { countryForumPath } from "../utils";
import { buildFeatures } from "./buildCountryFeatures";
import {
  clearSelection,
  getCircleColorExpression,
  setSelectedFeature,
  setupGlobeMap,
} from "./setupGlobeLayers";

interface HihGlobeMapProps {
  countries: CountryAssessments[];
  selectedIsoCode: string | null;
}

const FAO_BASEMAP_STYLE: StyleSpecification = {
  version: 8,
  glyphs: GLYPHS,
  sources: {
    "raster-tiles": {
      type: "raster",
      tiles: [FAO_OCEAN_TILES],
      tileSize: 256,
      attribution: "© FAO · Tiles © Esri",
    },
  },
  layers: [
    {
      id: "simple-tiles",
      type: "raster",
      source: "raster-tiles",
      minzoom: MIN_ZOOM - 1,
      maxzoom: MAX_ZOOM + 1,
    },
  ],
};

const HihGlobeMap = ({ countries, selectedIsoCode }: HihGlobeMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const countriesRef = useRef(countries);
  const selectedIsoRef = useRef(selectedIsoCode);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const [searchParams] = useSearchParams();
  const searchRef = useRef(searchParams.toString());
  countriesRef.current = countries;
  selectedIsoRef.current = selectedIsoCode;
  navigateRef.current = navigate;
  searchRef.current = searchParams.toString();

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: FAO_BASEMAP_STYLE,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      attributionControl: false,
    });

    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");

    const popup = new maplibregl.Popup({
      closeButton: true,
      closeOnClick: false,
      maxWidth: "320px",
      className: "hih-maplibre-popup",
    });

    const closePopup = () => {
      popup.remove();
      setSelectedFeature(map, selectedIsoRef.current);
    };

    const onLoad = () => {
      setupGlobeMap(map, countriesRef.current);
      setSelectedFeature(map, selectedIsoRef.current);
    };

    let markerClickedThisEvent = false;

    const openMarkerPopup = (feature: maplibregl.MapGeoJSONFeature) => {
      const coords = (feature.geometry as GeoJSON.Point).coordinates as [number, number];
      const html = feature.properties?.popupHTML as string | undefined;
      const isoCode = feature.properties?.iso_code as string | undefined;
      if (!html) return;

      setSelectedFeature(map, isoCode ?? null);
      popup.setLngLat(coords).setHTML(html).addTo(map);
    };

    const onMarkerClick = (e: maplibregl.MapLayerMouseEvent) => {
      const feature = e.features?.[0];
      if (!feature) return;
      markerClickedThisEvent = true;

      const hasAssessments = Boolean(feature.properties?.hasAssessments);
      const isoCode = feature.properties?.iso_code as string | undefined;

      if (hasAssessments && isoCode) {
        const search = searchRef.current ? `?${searchRef.current}` : "";
        navigateRef.current({ pathname: countryForumPath(isoCode), search });
        openMarkerPopup(feature);
        return;
      }

      openMarkerPopup(feature);
    };

    const onMapClick = () => {
      if (markerClickedThisEvent) {
        markerClickedThisEvent = false;
        return;
      }
      closePopup();
    };

    const onMarkerEnter = (e: maplibregl.MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = "pointer";
      const feature = e.features?.[0];
      if (!feature?.properties?.hasAssessments) return;
      map.setPaintProperty(VISUAL_LAYER, "circle-color", [
        "case",
        ["==", ["get", "iso_code"], feature.properties.iso_code],
        HOVER_COLOR,
        ["boolean", ["feature-state", "selected"], false],
        HOVER_COLOR,
        ["==", ["get", "hasAssessments"], true],
        ACTIVE_COLOR,
        INACTIVE_COLOR,
      ]);
    };

    const onMarkerLeave = () => {
      map.getCanvas().style.cursor = "";
      map.setPaintProperty(VISUAL_LAYER, "circle-color", getCircleColorExpression());
    };

    const onPopupClose = () => {
      setSelectedFeature(map, selectedIsoRef.current);
    };

    map.on("load", onLoad);
    map.on("click", VISUAL_LAYER, onMarkerClick);
    map.on("click", HIT_LAYER, onMarkerClick);
    map.on("click", LABELS_LAYER, onMarkerClick);
    map.on("click", onMapClick);
    MARKER_LAYERS.forEach((layer) => {
      map.on("mouseenter", layer, onMarkerEnter);
      map.on("mouseleave", layer, onMarkerLeave);
    });
    popup.on("close", onPopupClose);

    return () => {
      popup.off("close", onPopupClose);
      map.off("load", onLoad);
      map.off("click", VISUAL_LAYER, onMarkerClick);
      map.off("click", HIT_LAYER, onMarkerClick);
      map.off("click", LABELS_LAYER, onMarkerClick);
      map.off("click", onMapClick);
      MARKER_LAYERS.forEach((layer) => {
        map.off("mouseenter", layer, onMarkerEnter);
        map.off("mouseleave", layer, onMarkerLeave);
      });
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    if (!map.getSource(COUNTRIES_SOURCE)) return;

    (map.getSource(COUNTRIES_SOURCE) as maplibregl.GeoJSONSource).setData({
      type: "FeatureCollection",
      features: buildFeatures(countries),
    });
  }, [countries]);

  const prevSelectedIsoRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const applySelection = () => {
      if (!map.getSource(COUNTRIES_SOURCE)) return;

      const previousIso = prevSelectedIsoRef.current;
      prevSelectedIsoRef.current = selectedIsoCode;

      if (selectedIsoCode) {
        const centroid = countryCentroids[selectedIsoCode.toUpperCase()];
        setSelectedFeature(map, selectedIsoCode);
        if (centroid) {
          map.flyTo({
            center: centroid,
            zoom: COUNTRY_FOCUS_ZOOM,
            duration: FLY_DURATION_MS,
            essential: true,
          });
        }
        return;
      }

      clearSelection(map);
      // Only reset camera when leaving a country selection, not on first mount
      if (previousIso) {
        map.flyTo({
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          duration: FLY_DURATION_MS,
          essential: true,
        });
      }
    };

    if (map.isStyleLoaded() && map.getSource(COUNTRIES_SOURCE)) {
      applySelection();
      return;
    }

    map.once("load", applySelection);
  }, [selectedIsoCode]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "80vh",
      }}
      className="my-2 ov-hidden br-8"
    />
  );
};

export default HihGlobeMap;
